import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Icon, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import AILoaderRules from '../../../../shared/components/AILoaderRules';
import Colors from '../../../../../themes/Colors';
import PlayoutsLabelContainer from '../../containers/step2_settings/PlayoutsLabelContainer';
import HiddenByCpmTypeContainer from '../../../../shared/containers/HiddenByCpmTypeContainer';
import { getDomain } from '../../../../../services/utils';

const STEP = { SCOPE: 0, SCHEDULE: 1, HOURS: 2, LOADING: 3, SUMMARY: 4 };

const GROUP_BY_OPTIONS = [
  { key: 'country', label: 'Country' },
  { key: 'administrative_area_level_1', label: 'State / Province' },
  { key: 'locality', label: 'City' },
  { key: 'company_id', label: 'Company' },
];

const domain = getDomain(window.location.hostname);
const isLatinAd = domain === 'latinad';
const accentColor = isLatinAd ? '#60A5FA' : Colors.primary;
const bgGradient = isLatinAd
  ? 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 50%, #0F172A 100%)'
  : `linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 100%)`;

const modalBodyStyle = {
  background: bgGradient,
  borderRadius: '8px',
  padding: '24px',
  minHeight: '380px',
  display: 'flex',
  flexDirection: 'column',
};

function OptionCard({ selected, onClick, icon, title, description }) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer',
        padding: '14px 16px',
        borderRadius: '8px',
        border: `2px solid ${selected ? accentColor : 'rgba(255,255,255,0.1)'}`,
        background: selected ? `${accentColor}22` : 'rgba(255,255,255,0.04)',
        transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
        boxShadow: selected ? `0 0 14px ${accentColor}50` : 'none',
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: selected ? accentColor : 'rgba(255,255,255,0.1)',
          boxShadow: selected ? `0 0 10px ${accentColor}80` : 'none',
        }}
      >
        <Icon type={icon} style={{ color: Colors.white, fontSize: '18px' }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: Colors.white, fontWeight: '600', fontSize: '14px' }}>{title}</div>
        {description && (
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '2px' }}>
            {description}
          </div>
        )}
      </div>
      {selected && (
        <Icon type="check-circle" theme="filled" style={{ color: accentColor, fontSize: '20px', flexShrink: 0 }} />
      )}
    </div>
  );
}

function SummaryMetric({ icon, value, label }) {
  return (
    <div
      style={{
        flex: 1,
        background: `${accentColor}18`,
        border: `1px solid ${accentColor}40`,
        borderRadius: '8px',
        padding: '14px 8px',
        textAlign: 'center',
      }}
    >
      <Icon type={icon} style={{ fontSize: '22px', color: accentColor, display: 'block', marginBottom: '6px' }} />
      <div style={{ color: Colors.white, fontSize: '22px', fontWeight: 'bold', lineHeight: 1 }}>{value}</div>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px', marginTop: '5px' }}>{label}</div>
    </div>
  );
}

function StepIndicator({ current }) {
  const { t } = useTranslation();
  const labels = [t('Rule type'), t('Schedule'), t('Min. hours')];
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', marginBottom: '28px' }}>
      {labels.map((label, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <div
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: i < current ? accentColor : i === current ? `${accentColor}40` : 'rgba(255,255,255,0.08)',
                border: `2px solid ${i <= current ? accentColor : 'rgba(255,255,255,0.15)'}`,
                color: Colors.white,
                fontSize: '13px',
                fontWeight: 'bold',
                boxShadow: i === current ? `0 0 10px ${accentColor}60` : 'none',
              }}
            >
              {i < current ? <Icon type="check" /> : i + 1}
            </div>
            <span
              style={{
                color: i <= current ? accentColor : 'rgba(255,255,255,0.3)',
                fontSize: '11px',
                whiteSpace: 'nowrap',
                letterSpacing: '0.02em',
              }}
            >
              {label}
            </span>
          </div>
          {i < labels.length - 1 && (
            <div
              style={{
                flex: 1,
                height: '2px',
                maxWidth: '60px',
                background: i < current ? accentColor : 'rgba(255,255,255,0.1)',
                margin: '0 6px',
                marginBottom: '18px',
                transition: 'background 0.3s',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function DarkAlert({ icon, iconColor, borderColor, bgColor, style, children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 14px',
        borderRadius: '8px',
        border: `1px solid ${borderColor}`,
        borderLeft: `3px solid ${iconColor}`,
        background: bgColor,
        ...style,
      }}
    >
      <Icon
        type={icon}
        style={{ color: iconColor, fontSize: '16px', marginTop: '1px', flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function StepTitle({ title }) {
  return (
    <h3
      style={{
        color: Colors.white,
        textAlign: 'center',
        fontSize: '18px',
        fontWeight: '700',
        marginBottom: '8px',
        letterSpacing: '0.01em',
      }}
    >
      {title}
    </h3>
  );
}

function StepSubtitle({ subtitle }) {
  return (
    <p
      style={{
        color: 'rgba(255,255,255,0.55)',
        textAlign: 'center',
        fontSize: '13px',
        marginBottom: '20px',
        lineHeight: '1.5',
      }}
    >
      {subtitle}
    </p>
  );
}

export default function OptimizeRulesWizardComponent({
  visible,
  onClose,
  onApply,
  customContents,
  dateFrom,
  dateTo,
  onOptimizeRequest,
  loading,
  result,
  error,
}) {
  const { t } = useTranslation();

  const [step, setStep] = useState(STEP.SCOPE);
  const [ruleScope, setRuleScope] = useState(null);
  const [groupBy, setGroupBy] = useState(null);
  const [scheduleMode, setScheduleMode] = useState(null);
  const [minHours, setMinHours] = useState(5);
  const [animationDone, setAnimationDone] = useState(false);
  const [responseSeen, setResponseSeen] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const handleAnimationComplete = useCallback(() => setAnimationDone(true), []);

  useEffect(() => {
    if (step === STEP.LOADING && (result || error) && !responseSeen) {
      setResponseSeen(true);
    }
  }, [result, error, step, responseSeen]);

  useEffect(() => {
    if (step === STEP.LOADING && animationDone && responseSeen) {
      setStep(STEP.SUMMARY);
    }
  }, [animationDone, responseSeen, step]);

  const reset = () => {
    setStep(STEP.SCOPE);
    setRuleScope(null);
    setGroupBy(null);
    setScheduleMode(null);
    setMinHours(5);
    setAnimationDone(false);
    setResponseSeen(false);
    setConfirmVisible(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBack = () => {
    if (step > STEP.SCOPE) setStep(s => s - 1);
  };

  const handleNext = () => {
    if (step < STEP.HOURS) {
      setStep(s => s + 1);
    } else if (step === STEP.HOURS) {
      startCalculation();
    }
  };

  const isNextDisabled = () => {
    if (step === STEP.SCOPE) return !ruleScope || (ruleScope === 'group' && !groupBy);
    if (step === STEP.SCHEDULE) return !scheduleMode;
    if (step === STEP.HOURS) return !minHours;
    return false;
  };

  const startCalculation = () => {
    setStep(STEP.LOADING);
    setAnimationDone(false);
    setResponseSeen(false);

    onOptimizeRequest({
      start_date: dateFrom,
      end_date: dateTo,
      contents_displays: customContents.map(cc => ({
        display_id: cc.displayId,
        promised_shows: cc.promised_shows || 0,
      })),
      rule_scope: ruleScope,
      group_by: ruleScope === 'group' ? groupBy : null,
      schedule_mode: scheduleMode,
      min_hours_per_day: minHours,
    });
  };

  const handleApplyRules = () => {
    if (!result || !result.rules_by_display) return;

    const allVersions = [
      ...new Set(
        customContents
          .map(cc => cc.content && cc.content.content_version_name)
          .filter(Boolean)
      ),
    ];
    if (allVersions.length === 0) allVersions.push('V1');

    onApply(result.rules_by_display, allVersions);
    handleClose();
  };

  const renderScopeStep = () => (
    <div>
      <StepTitle title={t('Rule type')} />
      <StepSubtitle subtitle={t('Choose how to apply optimization rules across your screens')} />

      <OptionCard
        selected={ruleScope === 'general'}
        onClick={() => { setRuleScope('general'); setGroupBy(null); }}
        icon="global"
        title={t('General rule')}
        description={t('Apply the same rule to all campaign screens')}
      />
      <OptionCard
        selected={ruleScope === 'individual'}
        onClick={() => { setRuleScope('individual'); setGroupBy(null); }}
        icon="desktop"
        title={t('Individual rule')}
        description={t('Create a separate rule for each screen')}
      />
      <OptionCard
        selected={ruleScope === 'group'}
        onClick={() => setRuleScope('group')}
        icon="apartment"
        title={t('Group rule')}
        description={t('Group screens by a common criteria')}
      />

      {ruleScope === 'group' && (
        <div
          style={{
            marginTop: '14px',
            padding: '14px',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '8px',
            border: `1px solid ${accentColor}33`,
          }}
        >
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '10px', fontSize: '12px' }}>
            {t('Select grouping criteria')}:
          </p>
          {GROUP_BY_OPTIONS.map(opt => (
            <div
              key={opt.key}
              onClick={() => setGroupBy(opt.key)}
              style={{
                cursor: 'pointer',
                padding: '9px 12px',
                borderRadius: '6px',
                border: `1px solid ${groupBy === opt.key ? accentColor : 'rgba(255,255,255,0.1)'}`,
                background: groupBy === opt.key ? `${accentColor}1A` : 'transparent',
                color: groupBy === opt.key ? accentColor : 'rgba(255,255,255,0.65)',
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '13px',
                transition: 'all 0.15s',
              }}
            >
              {t(opt.label)}
              {groupBy === opt.key && <Icon type="check" style={{ color: accentColor }} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderScheduleStep = () => (
    <div>
      <StepTitle title={t('Schedule mode')} />
      <StepSubtitle subtitle={t('Choose the scheduling pattern for your rules')} />

      <OptionCard
        selected={scheduleMode === 'weekly'}
        onClick={() => setScheduleMode('weekly')}
        icon="calendar"
        title={t('Weekly schedule')}
        description={t('One rule covering the entire week with the same time slots')}
      />
      <OptionCard
        selected={scheduleMode === 'daily'}
        onClick={() => setScheduleMode('daily')}
        icon="clock-circle"
        title={t('Daily schedule')}
        description={t('Separate rules for each day of the week based on audience patterns')}
      />
    </div>
  );

  const renderHoursStep = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '16px' }}>
      <StepTitle title={t('Minimum active hours')} />
      <StepSubtitle
        subtitle={
          scheduleMode === 'daily'
            ? t('Minimum active hours per day')
            : t('Minimum active hours per week')
        }
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          marginTop: '24px',
        }}
      >
        <div className="optimize-wizard-hours-input">
          <InputNumber
            value={minHours}
            onChange={val => setMinHours(val)}
            parser={val => {
              const n = parseInt(String(val).replace(/[^0-9]/g, ''), 10);
              return isNaN(n) ? '' : n;
            }}
            style={{
              width: '110px',
              background: 'rgba(255,255,255,0.08)',
              border: `2px solid ${accentColor}`,
              borderRadius: '8px',
              boxShadow: `0 0 16px ${accentColor}50`,
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderSummary = () => {
    if (error) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Icon type="close-circle" theme="filled" style={{ fontSize: '52px', color: Colors.red, marginBottom: '16px' }} />
          <p style={{ color: Colors.white, fontSize: '16px' }}>{Array.isArray(error) ? error[0] : error}</p>
        </div>
      );
    }

    if (!result) return null;

    const { summary, displays_without_audience } = result;
    const allNoAudience =
      displays_without_audience &&
      displays_without_audience.length > 0 &&
      displays_without_audience.length >= customContents.length;

    return (
      <div style={{ paddingTop: '16px' }}>
        <StepTitle title={t('Optimization results')} />

        {displays_without_audience && displays_without_audience.length > 0 && (
          <DarkAlert
            icon="exclamation-circle"
            iconColor={Colors.atention}
            borderColor="rgba(245,158,11,0.35)"
            bgColor="rgba(245,158,11,0.08)"
            style={{ marginTop: '16px', marginBottom: '12px' }}
          >
            <span style={{ fontWeight: '600', color: Colors.white, display: 'block', marginBottom: '4px' }}>
              {t('Some screens have no audience data')}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px' }}>
              {t('The following screens could not be optimized')}:{' '}
              <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{displays_without_audience.join(', ')}</strong>
            </span>
          </DarkAlert>
        )}

        {!allNoAudience && summary && (
          <>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px', marginBottom: '16px' }}>
              <SummaryMetric
                icon="bars"
                value={summary.total_rules_created}
                label={t('Rules created')}
              />
              <HiddenByCpmTypeContainer>
                <SummaryMetric
                  icon="thunderbolt"
                  value={(summary.estimated_shows || 0).toLocaleString()}
                  label={<PlayoutsLabelContainer />}
                />
              </HiddenByCpmTypeContainer>
              <SummaryMetric
                icon="team"
                value={(summary.estimated_reach || 0).toLocaleString()}
                label={t('Estimated reach')}
              />
            </div>

            <DarkAlert
              icon="warning"
              iconColor={Colors.atention}
              borderColor="rgba(245,158,11,0.3)"
              bgColor="rgba(245,158,11,0.07)"
            >
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>
                {t('This action will replace all existing rules in the campaign')}
              </span>
            </DarkAlert>
          </>
        )}

        {allNoAudience && (
          <DarkAlert
            icon="info-circle"
            iconColor="rgba(255,255,255,0.5)"
            borderColor="rgba(255,255,255,0.15)"
            bgColor="rgba(255,255,255,0.05)"
            style={{ marginTop: '20px' }}
          >
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
              {t('No rules could be created as no screens have audience data available')}
            </span>
          </DarkAlert>
        )}
      </div>
    );
  };

  const renderWizardFooter = () => {
    const btnStyle = {
      borderRadius: '6px',
      fontWeight: '600',
      height: '36px',
    };

    const ghostBtn = {
      ...btnStyle,
      background: 'rgba(255,255,255,0.08)',
      borderColor: 'rgba(255,255,255,0.2)',
      color: Colors.white,
    };

    const primaryBtn = (disabled) => ({
      ...btnStyle,
      background: disabled ? 'rgba(255,255,255,0.12)' : accentColor,
      borderColor: disabled ? 'rgba(255,255,255,0.15)' : accentColor,
      color: Colors.white,
      boxShadow: disabled ? 'none' : `0 0 12px ${accentColor}60`,
    });

    if (step === STEP.SUMMARY) {
      const withoutAudience = result && result.displays_without_audience;
      const canApply =
        !error &&
        result &&
        result.rules_by_display &&
        (!withoutAudience || withoutAudience.length < customContents.length);

      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button onClick={handleClose} style={ghostBtn}>
            {t('Close')}
          </Button>
          {canApply && (
            <Button
              type="primary"
              style={primaryBtn(false)}
              onClick={() => setConfirmVisible(true)}
            >
              <Icon type="check" />
              {t('Apply rules')}
            </Button>
          )}
        </div>
      );
    }

    const isLast = step === STEP.HOURS;
    const disabled = isNextDisabled();

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
        <Button
          onClick={step === STEP.SCOPE ? handleClose : handleBack}
          style={ghostBtn}
        >
          {step === STEP.SCOPE ? t('Cancel') : t('Go back')}
        </Button>
        <Button
          type="primary"
          disabled={disabled}
          onClick={handleNext}
          style={primaryBtn(disabled)}
        >
          {isLast ? t('Calculate') : t('Continue')}
          {!isLast && <Icon type="arrow-right" />}
          {isLast && <Icon type="thunderbolt" />}
        </Button>
      </div>
    );
  };

  const renderConfirmModal = () => (
    <Modal
      visible={confirmVisible}
      title={t('Confirm optimization')}
      onOk={handleApplyRules}
      onCancel={() => setConfirmVisible(false)}
      okText={t('Apply rules')}
      cancelText={t('Cancel')}
      okButtonProps={{
        style: { background: accentColor, borderColor: accentColor },
      }}
    >
      <p style={{ fontSize: '15px', margin: '16px' }}>
        {t('All existing rules in the campaign will be replaced with the optimized ones. Do you wish to continue?')}
      </p>
    </Modal>
  );

  const stepContent = () => {
    switch (step) {
      case STEP.SCOPE:    return renderScopeStep();
      case STEP.SCHEDULE: return renderScheduleStep();
      case STEP.HOURS:    return renderHoursStep();
      case STEP.LOADING:  return <AILoaderRules onAnimationComplete={handleAnimationComplete} />;
      case STEP.SUMMARY:  return renderSummary();
      default:            return null;
    }
  };

  return (
    <>
      <Modal
        visible={visible}
        onCancel={step === STEP.LOADING ? undefined : handleClose}
        closable={step !== STEP.LOADING}
        maskClosable={step !== STEP.LOADING}
        footer={null}
        width={600}
        bodyStyle={{ padding: 0 }}
        style={{ top: 40 }}
        wrapClassName="optimize-rules-wizard-modal"
      >
        <div style={modalBodyStyle}>
          {step < STEP.LOADING && <StepIndicator current={step} />}
          <div style={{ flex: 1 }}>
            {stepContent()}
          </div>
          {step !== STEP.LOADING && (
            <div
              style={{
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: `1px solid rgba(255,255,255,0.1)`,
              }}
            >
              {renderWizardFooter()}
            </div>
          )}
        </div>
      </Modal>

      {renderConfirmModal()}

      <style>{`
        .optimize-rules-wizard-modal .ant-modal-content {
          background: transparent;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
          border-radius: 12px;
          overflow: hidden;
        }
        .optimize-rules-wizard-modal .ant-modal-body {
          padding: 0;
          background: transparent;
        }
        .optimize-rules-wizard-modal .ant-modal-close {
          color: rgba(255, 255, 255, 0.5);
          top: 10px;
          right: 10px;
        }
        .optimize-rules-wizard-modal .ant-modal-close:hover {
          color: ${Colors.white};
        }
        .optimize-rules-wizard-modal .ant-modal-close .ant-modal-close-x {
          color: inherit;
        }
        .optimize-wizard-hours-input .ant-input-number {
          background: rgba(255,255,255,0.08) !important;
          border: 2px solid ${accentColor} !important;
          border-radius: 8px !important;
          width: 120px !important;
          box-shadow: 0 0 16px ${accentColor}40 !important;
        }
        .optimize-wizard-hours-input .ant-input-number-handler-wrap {
          display: none !important;
        }
        .optimize-wizard-hours-input .ant-input-number-input-wrap {
          width: 100% !important;
        }
        .optimize-wizard-hours-input .ant-input-number-input {
          text-align: center !important;
          color: ${Colors.white} !important;
          font-size: 30px !important;
          font-weight: 700 !important;
          background: transparent !important;
          padding: 0 !important;
          width: 100% !important;
          letter-spacing: 0.02em !important;
        }
      `}</style>
    </>
  );
}
